import React, { useState } from "react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Upload, Loader2, CheckCircle2, Building2, MapPin, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

// Lista oficial de Regímenes Fiscales del SAT
const TAX_REGIMES = [
  { code: "601", name: "Régimen General de Ley Personas Morales" },
  { code: "602", name: "Régimen Simplificado de Ley Personas Morales" },
  { code: "603", name: "Personas Morales con Fines no Lucrativos" },
  { code: "605", name: "Régimen de Sueldos y Salarios e Ingresos Asimilados a Salarios" },
  { code: "606", name: "Régimen de Arrendamiento" },
  { code: "607", name: "Régimen de Enajenación o Adquisición de Bienes" },
  { code: "608", name: "Régimen de los Demás Ingresos" },
  { code: "611", name: "Régimen de Ingresos por Dividendos (Socios y Accionistas)" },
  { code: "612", name: "Régimen de las Personas Físicas con Actividades Empresariales y Profesionales" },
  { code: "614", name: "Régimen de los Ingresos por Intereses" },
  { code: "615", name: "Régimen de los Ingresos por Obtención de Premios" },
  { code: "616", name: "Sin Obligaciones Fiscales" },
  { code: "621", name: "Régimen de Incorporación Fiscal" },
  { code: "622", name: "Régimen de Actividades Agrícolas, Ganaderas, Silvícolas y Pesqueras PM" },
  { code: "625", name: "Régimen de las Actividades Empresariales con Ingresos a Través de Plataformas Tecnológicas" },
  { code: "626", name: "Régimen Simplificado de Confianza" },
];

const findMatchingRegime = (extractedText) => {
  if (!extractedText) return "";
  const norm = extractedText.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
  for (const r of TAX_REGIMES) {
    const rn = r.name.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
    if (norm.includes(rn) || rn.includes(norm)) return r.code;
    const terms = rn.split(" ").filter(t => t.length > 4);
    if (terms.filter(t => norm.includes(t)).length >= 2) return r.code;
  }
  return "";
};

export default function PhaseAddress({ data, onNext, onBack, loading, draftId }) {
  // "address_matches_csf" — does the client's current address match the CSF fiscal address?
  const [addressMatchesCSF, setAddressMatchesCSF] = useState(
    data.address_matches_csf !== undefined ? data.address_matches_csf : null
  );

  // Comprobante de domicilio states (only needed if address differs)
  const [proofFile, setProofFile] = useState(null);
  const [extractingProof, setExtractingProof] = useState(false);
  const [proofExtractionDone, setProofExtractionDone] = useState(false);

  // Address fields (filled from comprobante de domicilio, or from CSF fiscal address)
  const [addressData, setAddressData] = useState({
    street: data.street || data.fiscal_street || "",
    ext_number: data.ext_number || data.fiscal_ext_number || "",
    int_number: data.int_number || data.fiscal_int_number || "",
    neighborhood: data.neighborhood || data.fiscal_neighborhood || "",
    zip_code: data.zip_code || data.fiscal_zip_code || "",
    municipality: data.municipality || data.fiscal_municipality || "",
    state: data.state || data.fiscal_state || "",
  });

  // Fiscal data (carried from CSF extraction in PhaseDocuments)
  const [fiscalData, setFiscalData] = useState(() => {
    let regimeCode = data.tax_regime_code || "";
    if (!regimeCode && data.tax_regime) {
      regimeCode = findMatchingRegime(data.tax_regime);
    }
    const matched = TAX_REGIMES.find(r => r.code === regimeCode);
    return {
      tax_regime: matched ? matched.name : (data.tax_regime || ""),
      tax_regime_code: regimeCode,
      economic_activity: data.economic_activity || "",
      fiscal_street: data.fiscal_street || "",
      fiscal_ext_number: data.fiscal_ext_number || "",
      fiscal_int_number: data.fiscal_int_number || "",
      fiscal_neighborhood: data.fiscal_neighborhood || "",
      fiscal_zip_code: data.fiscal_zip_code || "",
      fiscal_municipality: data.fiscal_municipality || "",
      fiscal_state: data.fiscal_state || "",
    };
  });

  const handleAddressChange = (e) => {
    setAddressData({ ...addressData, [e.target.name]: e.target.value });
  };

  const handleFiscalChange = (e) => {
    setFiscalData({ ...fiscalData, [e.target.name]: e.target.value });
  };

  const handleRegimeChange = (e) => {
    const code = e.target.value;
    const regime = TAX_REGIMES.find(r => r.code === code);
    setFiscalData({ ...fiscalData, tax_regime_code: code, tax_regime: regime ? regime.name : "" });
  };

  // When user says address matches CSF, copy fiscal address to address fields
  const handleMatchesCSF = (matches) => {
    setAddressMatchesCSF(matches);
    if (matches) {
      // Use CSF fiscal address as the actual address
      setAddressData({
        street: fiscalData.fiscal_street,
        ext_number: fiscalData.fiscal_ext_number,
        int_number: fiscalData.fiscal_int_number,
        neighborhood: fiscalData.fiscal_neighborhood,
        zip_code: fiscalData.fiscal_zip_code,
        municipality: fiscalData.fiscal_municipality,
        state: fiscalData.fiscal_state,
      });
      setProofFile(null);
      setProofExtractionDone(false);
    } else {
      // Clear address for manual/extraction entry
      setAddressData({ street: "", ext_number: "", int_number: "", neighborhood: "", zip_code: "", municipality: "", state: "" });
    }
  };

  // Handle Comprobante de Domicilio upload
  const handleProofUpload = async (file) => {
    setProofFile(file);
    setExtractingProof(true);
    setProofExtractionDone(false);

    try {
      const extractForm = new FormData();
      extractForm.append("file", file);
      extractForm.append("doc_type", "comprobante_domicilio");

      const res = await axios.post(`${API}/api/document-extraction/extract`, extractForm, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" }
      });

      if (res.data.success) {
        const extracted = res.data.extracted_data || {};
        setAddressData(prev => ({
          street: extracted.street || prev.street,
          ext_number: extracted.ext_number || prev.ext_number,
          int_number: extracted.int_number || prev.int_number,
          neighborhood: extracted.neighborhood || prev.neighborhood,
          zip_code: extracted.zip_code || prev.zip_code,
          municipality: extracted.municipality || prev.municipality,
          state: extracted.state || prev.state,
        }));
        setProofExtractionDone(true);
        toast.success("Datos de domicilio extraídos correctamente");
      } else {
        toast.error(res.data.error || "No se pudieron extraer datos del comprobante");
      }

      if (draftId) {
        const uploadForm = new FormData();
        uploadForm.append("file", file);
        uploadForm.append("doc_type", "comprobante_domicilio");
        await axios.post(`${API}/api/customer-drafts/${draftId}/upload-document`, uploadForm, {
          withCredentials: true,
          headers: { "Content-Type": "multipart/form-data" }
        });
      }
    } catch (err) {
      console.error("Extraction error:", err);
      toast.error("Error al procesar el comprobante de domicilio");
    } finally {
      setExtractingProof(false);
    }
  };

  const handleContinue = () => {
    if (addressMatchesCSF === null) {
      toast.error("Indica si el domicilio actual coincide con el de la CSF");
      return;
    }

    // If address differs, comprobante is required
    if (!addressMatchesCSF) {
      if (!proofFile) {
        toast.error("Debes subir el comprobante de domicilio");
        return;
      }
      if (!addressData.street || !addressData.municipality || !addressData.state) {
        toast.error("Completa los campos de dirección obligatorios (calle, municipio, estado)");
        return;
      }
    }

    if (!fiscalData.tax_regime_code) {
      toast.error("Selecciona un régimen fiscal");
      return;
    }

    onNext({
      ...addressData,
      ...fiscalData,
      address_matches_csf: addressMatchesCSF,
    });
  };

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-xl font-semibold text-white mb-2">Domicilio y Datos Fiscales</h2>
        <p className="text-slate-400 text-sm">Confirma el domicilio y verifica los datos fiscales de la CSF</p>
      </div>

      <div className="max-w-3xl mx-auto space-y-6">

        {/* ── Fiscal Data from CSF ─────────────────────────── */}
        <div className="bg-slate-800/30 rounded-xl p-5 border border-slate-700">
          <div className="flex items-center gap-2 mb-4">
            <Building2 size={18} className="text-amber-400" />
            <h3 className="text-white font-medium">Datos Fiscales</h3>
            <span className="text-xs text-amber-400 bg-amber-500/10 px-2 py-0.5 rounded">Extraído de la CSF</span>
          </div>

          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <Label className="text-slate-300 text-xs">Régimen Fiscal *</Label>
                <select
                  value={fiscalData.tax_regime_code}
                  onChange={handleRegimeChange}
                  className="w-full h-9 px-3 text-sm bg-slate-800 border border-slate-700 rounded-md text-white focus:ring-2 focus:ring-teal-500"
                >
                  <option value="">Seleccionar régimen...</option>
                  {TAX_REGIMES.map((r) => (
                    <option key={r.code} value={r.code}>{r.code} - {r.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label className="text-slate-300 text-xs">Actividad Económica</Label>
                <Input name="economic_activity" value={fiscalData.economic_activity} onChange={handleFiscalChange} className="bg-slate-800 border-slate-700 text-white text-sm h-9" placeholder="Actividad principal" />
              </div>
              <div className="flex items-end">
                <div className="text-xs text-slate-500 p-2">
                  Los datos fiscales se extrajeron de la Constancia de Situación Fiscal subida en el paso anterior.
                </div>
              </div>
            </div>

            {/* Fiscal address (read-only display from CSF) */}
            <div className="border-t border-slate-700 pt-3">
              <p className="text-slate-400 text-xs mb-2">Domicilio Fiscal (del SAT)</p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                {[
                  ["Calle", "fiscal_street"], ["Núm. Ext.", "fiscal_ext_number"],
                  ["Colonia", "fiscal_neighborhood"], ["C.P.", "fiscal_zip_code"],
                  ["Municipio", "fiscal_municipality"], ["Estado", "fiscal_state"],
                ].map(([label, key]) => (
                  <div key={key}>
                    <Label className="text-slate-400 text-xs">{label}</Label>
                    <Input name={key} value={fiscalData[key]} onChange={handleFiscalChange} className="bg-slate-800 border-slate-700 text-white text-sm h-8" />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* ── Address Match Question ─────────────────────────── */}
        <div className="bg-slate-800/50 rounded-xl p-5 border border-slate-700">
          <p className="text-white font-medium mb-4">¿El domicilio actual del cliente es el mismo que aparece en la CSF?</p>
          <div className="flex gap-4">
            <Button
              type="button"
              onClick={() => handleMatchesCSF(true)}
              variant={addressMatchesCSF === true ? "default" : "outline"}
              className={addressMatchesCSF === true ? "bg-teal-600" : "border-slate-700"}
            >
              Sí, es el mismo
            </Button>
            <Button
              type="button"
              onClick={() => handleMatchesCSF(false)}
              variant={addressMatchesCSF === false ? "default" : "outline"}
              className={addressMatchesCSF === false ? "bg-teal-600" : "border-slate-700"}
            >
              No, es diferente
            </Button>
          </div>
          {addressMatchesCSF === true && (
            <p className="text-xs text-teal-400 mt-3">✓ Se usará el domicilio fiscal de la CSF como domicilio actual.</p>
          )}
        </div>

        {/* ── Comprobante de Domicilio (only if address differs) ── */}
        {addressMatchesCSF === false && (
          <div className="space-y-5">
            {/* Upload comprobante */}
            <div className="bg-slate-800/30 rounded-xl p-5 border border-amber-500/30">
              <div className="flex items-start gap-3 mb-4">
                <MapPin className="text-teal-400 mt-0.5" size={20} />
                <div>
                  <h3 className="text-white font-medium text-sm">Comprobante de Domicilio *</h3>
                  <p className="text-slate-400 text-xs mt-1">Recibo de luz, agua, teléfono o estado de cuenta (máx. 3 meses)</p>
                </div>
              </div>

              <div className="border-2 border-dashed border-slate-600 rounded-lg p-4 text-center hover:border-teal-500/50 transition-colors">
                <Upload className="mx-auto mb-2 text-slate-400" size={24} />
                <input
                  type="file"
                  accept="image/*,application/pdf"
                  onChange={(e) => e.target.files[0] && handleProofUpload(e.target.files[0])}
                  className="hidden"
                  id="proof-address"
                  disabled={extractingProof}
                />
                <label
                  htmlFor="proof-address"
                  className={`inline-flex items-center justify-center px-3 py-1.5 text-xs font-medium rounded-md border transition-colors ${
                    extractingProof
                      ? "border-slate-600 bg-slate-700 text-slate-400 cursor-not-allowed"
                      : proofFile
                        ? "border-teal-500 bg-teal-500/10 text-teal-400 cursor-pointer"
                        : "border-slate-700 bg-transparent text-white cursor-pointer hover:bg-slate-800"
                  }`}
                >
                  {extractingProof ? (
                    <><Loader2 size={14} className="mr-1.5 animate-spin" />Extrayendo...</>
                  ) : proofFile ? (
                    <><CheckCircle2 size={14} className="mr-1.5 text-teal-400" />Subido ✓</>
                  ) : "Seleccionar archivo"}
                </label>
              </div>

              {proofExtractionDone && (
                <div className="mt-3 flex items-center gap-1.5 text-xs text-teal-400 bg-teal-500/10 rounded-lg p-2">
                  <CheckCircle2 size={14} /> Datos de dirección extraídos
                </div>
              )}
            </div>

            {/* Address fields */}
            <div className="bg-slate-800/30 rounded-xl p-5 border border-slate-700">
              <div className="flex items-center gap-2 mb-4">
                <MapPin size={18} className="text-teal-400" />
                <h3 className="text-white font-medium">Domicilio Actual</h3>
                {proofExtractionDone && (
                  <span className="text-xs text-teal-400 bg-teal-500/10 px-2 py-0.5 rounded">Autocompletado</span>
                )}
              </div>

              <div className="space-y-3">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                  <div className="md:col-span-2">
                    <Label className="text-slate-300 text-xs">Calle *</Label>
                    <Input name="street" value={addressData.street} onChange={handleAddressChange} className="bg-slate-800 border-slate-700 text-white text-sm h-9" placeholder="Nombre de la calle" />
                  </div>
                  <div>
                    <Label className="text-slate-300 text-xs">Núm. Ext.</Label>
                    <Input name="ext_number" value={addressData.ext_number} onChange={handleAddressChange} className="bg-slate-800 border-slate-700 text-white text-sm h-9" />
                  </div>
                  <div>
                    <Label className="text-slate-300 text-xs">Núm. Int.</Label>
                    <Input name="int_number" value={addressData.int_number} onChange={handleAddressChange} className="bg-slate-800 border-slate-700 text-white text-sm h-9" />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                  <div>
                    <Label className="text-slate-300 text-xs">Colonia</Label>
                    <Input name="neighborhood" value={addressData.neighborhood} onChange={handleAddressChange} className="bg-slate-800 border-slate-700 text-white text-sm h-9" />
                  </div>
                  <div>
                    <Label className="text-slate-300 text-xs">C.P.</Label>
                    <Input name="zip_code" value={addressData.zip_code} onChange={handleAddressChange} className="bg-slate-800 border-slate-700 text-white text-sm h-9" />
                  </div>
                  <div>
                    <Label className="text-slate-300 text-xs">Municipio *</Label>
                    <Input name="municipality" value={addressData.municipality} onChange={handleAddressChange} className="bg-slate-800 border-slate-700 text-white text-sm h-9" />
                  </div>
                  <div>
                    <Label className="text-slate-300 text-xs">Estado *</Label>
                    <Input name="state" value={addressData.state} onChange={handleAddressChange} className="bg-slate-800 border-slate-700 text-white text-sm h-9" />
                  </div>
                </div>
              </div>
            </div>

            <div className="flex items-start gap-2 text-sm text-slate-400 bg-slate-800/50 rounded-lg p-3">
              <AlertCircle size={16} className="text-amber-400 mt-0.5 shrink-0" />
              <span>El comprobante de domicilio es obligatorio cuando la dirección difiere de la CSF.</span>
            </div>
          </div>
        )}
      </div>

      <div className="flex justify-between mt-8">
        <Button onClick={onBack} variant="outline" className="border-slate-700">
          ← Atrás
        </Button>
        <Button
          onClick={handleContinue}
          disabled={loading || extractingProof}
          className="bg-teal-600 hover:bg-teal-500 text-white"
        >
          {extractingProof ? (
            <><Loader2 size={16} className="mr-2 animate-spin" />Procesando...</>
          ) : "Continuar →"}
        </Button>
      </div>
    </div>
  );
}
